(() => {
  const register = () => {
    if (typeof ManagedHTMLElement === 'undefined' || customElements.get('image-feature-video')) return;
    customElements.define('image-feature-video', class extends ManagedHTMLElement {
      connectedCallback() {
        this.control = this.querySelector('.image-feature-panel__playback');
        if (!this.control) { this.requestFrame(() => this.connectedCallback()); return; }
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.visible = false;
        this.userPaused = false;
        this.userStarted = false;
        this._listenerController ||= new AbortController();
        const signal = this._listenerController.signal;
        const loadPreview = () => {
          if (!this.isConnected || signal.aborted) return;
          this.previewObserver?.disconnect();
          for (const image of this.querySelectorAll('[data-feature-srcset], [data-feature-src]')) {
            for (const attribute of ['srcset', 'src']) {
              const value = image.getAttribute('data-feature-' + attribute);
              if (value) image.setAttribute(attribute, value);
              image.removeAttribute('data-feature-' + attribute);
            }
            image.removeAttribute('aria-hidden');
          }
        };
        if (this.querySelector('[data-feature-src]')) {
          this.listenTo(this, 'focusin', loadPreview, { once: true });
          if ('IntersectionObserver' in window) {
            this.previewObserver = new IntersectionObserver(entries => {
              if (entries.some(entry => entry.isIntersecting)) loadPreview();
            }, { rootMargin: '600px 0px' });
            this.previewObserver.observe(this);
          } else loadPreview();
        }
        this.control.hidden = false;
        this.listenTo(this.control, 'click', () => {
          this.userPaused = !!this.video && !this.video.paused;
          this.userStarted = !this.userPaused;
          if (this.video?.error) this.removeVideo();
          this.update();
        });
        this.listenTo(this.motion, 'change', () => { this.userStarted = false; this.update(); });
        this.listenTo(document, 'visibilitychange', () => this.update());
        this.listenTo(this, 'playing', event => {
          if (event.target !== this.video || this.video.paused || this.video.error) return;
          this.video.dataset.ready = '';
          this.userPaused = false;
          this.updateControl();
        }, { capture: true });
        this.listenTo(this, 'pause', event => {
          if (event.target !== this.video) return;
          if (this.visible && !document.hidden) this.userPaused = true;
          this.updateControl();
        }, { capture: true });
        this.listenTo(this, 'ended', () => { this.userPaused = true; this.updateControl(); }, { capture: true });
        this.listenTo(this, 'error', event => {
          if (event.target !== this.video) return;
          this.video.pause();
          delete this.video.dataset.ready;
          this.userPaused = true;
          this.updateControl();
        }, { capture: true });
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(entries => {
            if (signal.aborted) return;
            this.visible = entries.some(entry => entry.isIntersecting);
            this.update();
          });
          this.observer.observe(this);
        } else {
          this.visible = true;
          this.update();
        }
        this.updateControl();
      }
      update() {
        if (!this.isConnected) return;
        const autoplay = this.dataset.autoplay === 'true' && !this.motion.matches;
        if (!this.visible || document.hidden || this.userPaused || !autoplay && !this.userStarted) {
          this.video?.pause();
          this.updateControl();
          return;
        }
        if (!this.video) {
          this.video = this.querySelector('[data-feature-video]').content.firstElementChild.cloneNode(true);
          if (this.video.dataset.featurePoster) this.video.poster = this.video.dataset.featurePoster;
          this.video.muted = true;
          this.video.controls = !autoplay;
          this.control.toggleAttribute('data-native', !autoplay);
          this.append(this.video);
          this.video.load();
        }
        const video = this.video;
        if (video.paused) video.play().catch(error => {
          if (this.video !== video || error.name === 'AbortError') return;
          delete video.dataset.ready;
          this.userPaused = true;
          this.updateControl();
        });
        this.updateControl();
      }
      updateControl() {
        const playing = !!this.video && !this.video.paused && !this.video.ended && !this.video.error;
        this.control.toggleAttribute('data-playing', playing);
        this.control.setAttribute('aria-label', playing ? this.control.dataset.pauseLabel : this.control.dataset.playLabel);
      }
      removeVideo() {
        this.video?.pause();
        this.video?.remove();
        this.video = null;
      }
      disconnectedCallback() {
        this.previewObserver?.disconnect();
        this.observer?.disconnect();
        this.removeVideo();
        if (this.control) this.control.hidden = true;
        super.disconnectedCallback();
      }
    });
  };
  if (typeof ManagedHTMLElement !== 'undefined') register();
  else document.addEventListener('DOMContentLoaded', register, { once: true });
})();
